import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StepService } from 'services/professional/step/step.service';
import { MessageQueryService, SmartModalService } from '@certemy/ui-components';
import { DynamicFormInfo } from 'interfaces/dynamic-form-info.interface';
import { ComputedFrom } from '@certemy/common';
import { isStepSubmitted } from 'utils/step.helpers';
import { DynamicFormField, DynamicFormSection } from 'interfaces/dynamic-form-field.interface';
import { DynamicFormContentDetails } from '../dynamic-details/modal-content/dynamic-form-content-details/dynamic-form-content-details.component';
import { constants, VERIFICATION_TYPE } from 'constants/constants';
import { SignatureComponent } from 'components/signature/signature.component';
import { DynamicFormComponent } from 'components/dynamic-form/dynamic-form.component';
import _uniq from 'lodash-es/uniq';
import _get from 'lodash-es/get';
import { filter, take } from 'rxjs/operators';
import {
  getLicenseStatusType,
  LICENSE_STATUS_CONFIRMATION_FORM,
  LICENSE_VERIFICATION_DOCUMENTATION_TRACKED_FIELDS,
  LICENSE_VERIFICATION_DOCUMENTATION_TOOLTIP_MAP,
  validateVerificationDocumentationStep,
  setValidators,
  setTrackedFields,
} from 'constants/license-step.constant';
import { DynamicFormInfoToSave } from 'components/modal-windows/step-details/dynamic-details/dynamic-details.constants';
import { STEP_TYPES } from 'constants/status.constants';
import { replaceTemplateFromObject } from 'utils/helpers';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-license-details',
  styleUrls: ['./license-details.modal.component.scss'],
  templateUrl: './license-details.modal.component.html',
})
export class LicenseDetailsModalComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() step;
  @Output() updateStep = new EventEmitter();
  @Output() toggleMode: EventEmitter<string> = new EventEmitter();
  @ViewChild('signature') signatureComponent: SignatureComponent;
  @ViewChild(DynamicFormComponent) dynamicFormComponent: DynamicFormComponent;

  formInfo: DynamicFormInfo = null;
  private signature_link: string;
  showErrors = false;
  sections: DynamicFormSection[];
  formErrors: string[] = [];
  modalDetails: DynamicFormContentDetails;
  afterView: boolean = false;
  trackedFields: { [key in string]: DynamicFormField } = {};

  readonly STEP_TYPES = STEP_TYPES;

  @ComputedFrom('step')
  get isStepSubmitted() {
    return isStepSubmitted(this.step.status_id);
  }

  constructor(
    public activeModal: NgbActiveModal,
    private stepService: StepService,
    private messageQueryService: MessageQueryService,
    private smartModalService: SmartModalService,
  ) {}

  ngOnInit() {
    this.modalDetails = {
      stepDescription: this.step.description,
    };

    const { signature_link, terms, sections, exams } = this.step.content;
    const fields = _get(this.step, 'content.forms[0].fields', []);
    setTrackedFields(fields, this.step.typeId, this.trackedFields);
    setValidators(fields, this.step.specialization_label);
    this.sections = sections;
    this.signature_link = signature_link;
    this.modalDetails.disclosureText = terms;

    if (!this.isStepSubmitted) {
      this.stepService
        .getDynamicFormData(this.step.step_id)
        .pipe(untilDestroyed(this))
        .subscribe(
          (formInfo: DynamicFormInfo) =>
            (this.formInfo = {
              ...formInfo,
              fields: this.stepService.mapFormInfoFields(_get(this.step, 'content.forms[0].fields', []), formInfo),
            }),
        );
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.afterView = true;
      setTimeout(() => {
        if (this.isStepSubmitted && this.step.content.signature_link) {
          this.signatureComponent.signature = this.step.content.signature_link;
        }
      });
    });
  }

  ngOnDestroy() {}

  @ComputedFrom('isStepSubmitted', 'step', 'formInfo')
  get elements() {
    const elems = !this.isStepSubmitted && this.formInfo ? this.formInfo.fields : this.step.content.forms[0].fields;
    return elems.map((element) => {
      try {
        const {
          type: { id: typeId },
          subType,
        } = element;
        const tooltip = LICENSE_VERIFICATION_DOCUMENTATION_TOOLTIP_MAP[typeId][subType].replace(
          /{specialization_label}/g,
          this.step.specialization_label,
        );
        return {
          ...element,
          tooltip,
        };
      } catch (e) {
        return element;
      }
    });
  }

  closeModal() {
    this.activeModal.close();
  }

  dismissModal() {
    this.activeModal.dismiss();
  }

  private isVerificationRequired() {
    return [VERIFICATION_TYPE.VERIFICATION_REQUIRED, VERIFICATION_TYPE.DOUBLE_VERIFICATION_REQUIRED].includes(
      this.step.verificationType,
    );
  }

  submitForm() {
    const signatureErrorsMessage = [];
    this.formErrors = [];
    this.showErrors = false;
    if (this.signatureComponent) {
      const stepSignatureError = this.signatureComponent.getSignatureErrors();

      if (stepSignatureError) {
        signatureErrorsMessage.push(stepSignatureError);
      } else {
        this.signature_link = this.signatureComponent.signature;
      }
    }

    const regForm = this.dynamicFormComponent.dynamicForm;
    if (this.step.typeId === STEP_TYPES.LICENSE_VERIFICATION) {
      validateVerificationDocumentationStep(regForm, this.trackedFields);
    }
    if (regForm.valid && !signatureErrorsMessage.length) {
      const values = regForm.value;
      if (this.trackedFields[LICENSE_VERIFICATION_DOCUMENTATION_TRACKED_FIELDS.STATUS]) {
        const availableStatuses = _get(this.step, 'content.availableStatuses', []);
        const licenseStatusType = getLicenseStatusType(
          values[this.trackedFields[LICENSE_VERIFICATION_DOCUMENTATION_TRACKED_FIELDS.STATUS].id],
          this.trackedFields[LICENSE_VERIFICATION_DOCUMENTATION_TRACKED_FIELDS.STATUS],
          availableStatuses,
        );
        const body = replaceTemplateFromObject(
          LICENSE_STATUS_CONFIRMATION_FORM[this.step.step_type_id][licenseStatusType],
          'specialization_label',
          this.step,
          'credential',
        );
        return this.smartModalService
          .openConfirm({
            title: 'Attention',
            body,
            successButtonText: 'Continue',
          })
          .pipe(
            take(1),
            filter((canDeactivate) => canDeactivate),
          )
          .subscribe((response) => {
            this.completeStep({ formValue: values });
          });
      }
      return this.completeStep({ formValue: values });
    } else {
      this.showErrors = true;
      this.formErrors = this.dynamicFormComponent.getFormErrorMessages();
      if (signatureErrorsMessage.length) {
        this.formErrors.push(_uniq(signatureErrorsMessage) as any);
      }
    }
  }

  private completeStep(form) {
    const dataToSend: DynamicFormInfoToSave = {
      saveAsDraft: false,
      forms: [form],
      id: this.step.step_instance_id,
      signature_link: this.signature_link,
    };

    return this.stepService
      .submitDynamicForm(dataToSend)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.stepCompletedMessage());
  }

  private stepCompletedMessage() {
    const messageText = this.isVerificationRequired()
      ? 'Step has been submitted for review'
      : 'Step has been successfully completed';
    this.messageQueryService.addMessage(messageText);
    this.closeModal();
  }

  updateFieldValue({ field: { id, value }, elements, controls }) {
    try {
      const {
        type: { id: typeId },
        subType,
      } = elements.find(({ id: elementId }) => elementId === Number(id));
      if (typeId === constants.FORM_FIELD_TYPE_IDS.TEXT && subType === constants.TEXT_SUB_TYPES.LICENSE_NUMBER) {
        const control = controls[id];
        if (new RegExp(/\s/).test(value)) {
          control.setValue(value.replace(/\s/g, ''));
        }
      }
    } catch (e) {}
  }
}
